import * as THREE from "three";

type ShowPartParams = {
  model: THREE.Group;
  scene: THREE.Scene;
  partName: string;
  modelName: string;
  findNodesByName: (name: string) => THREE.Object3D[];
  normalize: (name: string) => string;
};

/**
 * Finds and shows a part of the model by name
 */
export function showPart(params: ShowPartParams): boolean {
  const { model, scene, partName, modelName, findNodesByName, normalize } =
    params;

  if (!model) return false;

  const isNone = !modelName || normalize(modelName) === "none";
  const searchModelName = normalize(modelName);

  // Find all nodes matching the part name
  const categoryNodes = findNodesByName(partName);
  if (categoryNodes.length === 0) return false;

  // Find specific nodes matching the model name
  const selectedNodes: THREE.Object3D[] = [];
  if (!isNone && searchModelName) {
    for (const node of categoryNodes) {
      if (!node || typeof node.name !== "string" || !node.name) continue;
      const nodeName = normalize(node.name);
      if (nodeName.includes(searchModelName)) {
        selectedNodes.push(node);
      }
    }
  }

  // Hide all category nodes first
  for (const node of categoryNodes) {
    node.visible = false;
    node.traverse((descendant) => {
      descendant.visible = false;
    });
  }

  // Show only selected nodes
  if (!isNone && selectedNodes.length > 0) {
    for (const node of selectedNodes) {
      node.visible = true;
      // Ensure parent chain is visible
      let parent = node.parent;
      while (parent && parent !== scene) {
        parent.visible = true;
        parent = parent.parent;
      }
      // Make all descendants visible
      node.traverse((descendant) => {
        descendant.visible = true;
        if (descendant instanceof THREE.Mesh) {
          if (descendant.material instanceof THREE.Material) {
            descendant.material.visible = true;
          } else if (Array.isArray(descendant.material)) {
            descendant.material.forEach((mat) => {
              if (mat instanceof THREE.Material) {
                mat.visible = true;
              }
            });
          }
        }
      });
    }
  }

  return true;
}
